import React, { useMemo, useCallback } from 'react';
import { ProfessionalTable } from './ProfessionalTable';
import { DatasetHeader } from './DatasetHeader';
import { ChatPanelContainer } from './ChatPanelContainer';
import { MonitorButtonRef } from '../monitoring/MonitorButton';
import { UseTableDataReturn, TableState } from '../../hooks/dashboard';
import { UseChatWorkflowsReturn } from '../../hooks/dashboard/useChatWorkflows';
import { UseRowSelectionReturn } from '../../hooks/dashboard/useRowSelection';
import { UseDashboardModalsReturn } from '../../hooks/dashboard/useDashboardModals';
import { IdealCustomerProfile } from '../../types';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { transition } from '../../styles/tokens';

export interface DatasetViewLayoutProps {
  tableId: string;
  tableData: UseTableDataReturn;
  chatWorkflows: UseChatWorkflowsReturn;
  rowSelection: UseRowSelectionReturn;
  modals: UseDashboardModalsReturn;
  tableStates: Record<string, TableState>;
  onTableStateChange: (tableId: string, state: TableState) => void;
  chatIcpContext: IdealCustomerProfile | null;
  pendingAction: { type: string; payload?: any } | null;
  professionalTableRef: React.RefObject<any>;
  monitorButtonRef: React.RefObject<MonitorButtonRef>;
  // Handlers
  onShare: () => void;
  onExportCSV: () => void;
  onExportExcel: () => void;
  onDelete: () => void;
  onCreateWorkflow: () => void;
  handleCreateDataset: (
    searchQuery: string | null,
    criteria: string[] | null,
    tableType: 'companies' | 'people' | 'custom',
    customType: string,
    rowCount: number,
    uploadId: string | null,
    title: string | null
  ) => Promise<void>;
  // Initial messages from navigation
  initialMessages?: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    criteria?: string[];
  }>;
}

export const DatasetViewLayout: React.FC<DatasetViewLayoutProps> = ({
  tableId,
  tableData,
  chatWorkflows,
  rowSelection,
  modals,
  tableStates,
  onTableStateChange,
  chatIcpContext,
  pendingAction,
  professionalTableRef,
  monitorButtonRef,
  onShare,
  onExportCSV,
  onExportExcel,
  onDelete,
  onCreateWorkflow,
  handleCreateDataset,
  initialMessages,
}) => {
  const isMobile = useIsMobile();

  // Memoize context objects for chat panel
  const tableContext = useMemo(() => ({
    name: tableData.name,
    type: tableData.type,
    columns: tableData.columns,
    status: tableData.status,
    description: tableData.tableData?.description || tableData.tableData?.table_description,
    criteria: tableData.tableData?.provider_metadata?.criteria,
    monitors: tableData.monitors,
    data: {
      provider_metadata: {
        total_analyzed: tableData.tableData?.provider_metadata?.total_analyzed || 0,
        total_found: tableData.tableData?.provider_metadata?.total_found || 0,
      }
    }
  }), [tableData.name, tableData.type, tableData.columns, tableData.status, tableData.tableData, tableData.monitors]);

  const enrichmentContext = useMemo(() => ({
    rowCount: tableData.leads.length,
    columns: tableData.columns.map(c => typeof c === 'string' ? c : c.name || c.key),
    preSelectedColumns: chatWorkflows.enrichmentActionParams?.columns ||
      (chatWorkflows.enrichmentActionParams?.column ? [chatWorkflows.enrichmentActionParams.column] : undefined),
    triggeredFromChat: chatWorkflows.enrichmentTriggeredFromChat,
  }), [tableData.leads.length, tableData.columns, chatWorkflows.enrichmentActionParams, chatWorkflows.enrichmentTriggeredFromChat]);

  const findMoreContext = useMemo(() => ({
    currentCount: tableData.leads.length,
    initialCount: chatWorkflows.findMoreActionParams?.count,
    triggeredFromChat: chatWorkflows.findMoreTriggeredFromChat,
  }), [tableData.leads.length, chatWorkflows.findMoreActionParams, chatWorkflows.findMoreTriggeredFromChat]);

  const campaignContext = useMemo(() => ({
    totalLeadCount: tableData.leads.length,
    filteredLeadCount: undefined,
    selectedLeadCount: rowSelection.selectedRowIds.size > 0 ? rowSelection.selectedRowIds.size : undefined,
    selectedLeadIds: rowSelection.selectedRowIds.size > 0 ? Array.from(rowSelection.selectedRowIds) : undefined,
    hasActiveFilters: false,
    rows: tableData.leads,
  }), [tableData.leads, rowSelection.selectedRowIds]);

  // Handlers
  const handleMonitorSetupComplete = useCallback((keepMessages?: boolean) => {
    if (!keepMessages) {
      chatWorkflows.closeMonitorSetup();
    }
    monitorButtonRef.current?.refresh();
  }, [chatWorkflows, monitorButtonRef]);

  const handleCampaignCreated = useCallback(() => {
    chatWorkflows.closeCampaignCreation();
    rowSelection.clearSelection();
    // Summary is already persisted to chat history by the CampaignCreationFlow via onAddMessages
    // Navigation to campaign is handled by the "View Campaign" button in CompleteStep
    // "Done" button keeps user in the dataset view
  }, [chatWorkflows, rowSelection]);

  const handleCampaignCancel = useCallback(() => {
    chatWorkflows.closeCampaignCreation();
    rowSelection.clearSelection();
  }, [chatWorkflows, rowSelection]);

  const handleCampaignEnrichData = useCallback((missingColumns: string[]) => {
    chatWorkflows.closeCampaignCreation();
    rowSelection.clearSelection();
    chatWorkflows.startEnrichmentFlow({ columns: missingColumns }, true);
  }, [chatWorkflows, rowSelection]);

  const handleEnrichmentStarted = useCallback((columnName: string) => {
    console.log('Enrichment started for column:', columnName);
  }, []);

  const handleFindMoreStarted = useCallback((count: number) => {
    console.log('Find more started for count:', count);
  }, []);

  const handleUpdatedDataset = useCallback((tableId: string) => {
    tableData.updateDataset(tableId);
  }, [tableData]);

  const handleStateChange = useCallback((state: TableState) => {
    onTableStateChange(tableId, state);
  }, [tableId, onTableStateChange]);

  return (
    <div className="flex-1 flex flex-row min-w-0 overflow-hidden">
      {/* Table area */}
      <div
        className="flex-1 flex flex-col min-w-0 overflow-hidden"
        style={{ transition: `all ${transition.duration.panel} ${transition.timing.panel}` }}
      >
        {/* Table Header - Title + Actions */}
        {!isMobile && (
          <DatasetHeader
            tableName={tableData.name}
            leadCount={tableData.leads.length}
            tableId={tableId}
            websetId={tableData.tableData?.provider_metadata?.webset_id}
            monitorButtonRef={monitorButtonRef}
            exportMenuRef={modals.exportMenuRef}
            showExportMenu={modals.showExportMenu}
            onToggleExportMenu={modals.toggleExportMenu}
            onCloseExportMenu={modals.closeExportMenu}
            onShare={onShare}
            onExportCSV={onExportCSV}
            onExportExcel={onExportExcel}
            onDelete={onDelete}
            onStartMonitorSetup={chatWorkflows.startMonitorSetup}
          />
        )}
        <div className="flex-1 overflow-auto" data-tour="lead-table-content">
          <ProfessionalTable
            key={tableId}
            ref={professionalTableRef}
            leads={tableData.leads}
            tableType={tableData.type}
            tableId={tableId}
            tableStatus={tableData.status}
            tableData={tableData.tableData}
            tableName={tableData.name}
            errorMessage={tableData.errorMessage}
            isLoadingInitialData={tableData.isLoading}
            onDataRefresh={tableData.refreshData}
            prospectsAnalyzed={tableData.prospectsAnalyzed}
            initialSortRules={tableStates[tableId]?.sortRules}
            initialFullMatchesFirst={tableStates[tableId]?.fullMatchesFirst}
            initialFilterConditions={tableStates[tableId]?.filterConditions}
            onStateChange={handleStateChange}
            onUpdatedDataset={handleUpdatedDataset}
            handleCreateDataset={handleCreateDataset}
            isChatOpen={chatWorkflows.isChatPanelOpen}
            pendingAction={pendingAction}
            hideHeader={true}
            onShare={onShare}
            onExportCSV={onExportCSV}
            onExportExcel={onExportExcel}
            onDelete={onDelete}
            rowSelectionMode={rowSelection.rowSelectionMode}
            selectedRowIds={rowSelection.selectedRowIds}
            onRowSelectionChange={rowSelection.setSelectedRowIds}
            visualSelectionMode={rowSelection.visualSelectionMode}
            visualSelectionCount={rowSelection.visualSelectionCount}
            onProgressUpdate={tableData.setProgressData}
            onStartEnrichmentFlow={() => chatWorkflows.startEnrichmentFlow(null, false)}
            onStartCampaignFlow={(options) => {
              // Pre-select the rows if provided
              if (options?.selectedRowIds && options.selectedRowIds.size > 0) {
                rowSelection.setSelectedRowIds(options.selectedRowIds);
              }
              chatWorkflows.startCampaignCreation();
            }}
          />
        </div>
      </div>

      {/* Chat Panel */}
      <ChatPanelContainer
        isOpen={chatWorkflows.isChatPanelOpen}
        onClose={chatWorkflows.closeChatPanel}
        onOpen={chatWorkflows.openChatPanel}
        tableId={tableId}
        tableContext={tableContext}
        icpContext={chatIcpContext}
        leadCount={tableData.leads.length}
        progressData={tableData.progressData ?? undefined}
        initialMessages={initialMessages}
        onActionTriggered={chatWorkflows.handleChatActionTrigger}
        onExportCSV={onExportCSV}
        onExportExcel={onExportExcel}
        onShare={onShare}
        onDelete={onDelete}
        onCreateWorkflow={onCreateWorkflow}
        showMonitorSetup={chatWorkflows.showMonitorSetup}
        onMonitorSetupComplete={handleMonitorSetupComplete}
        onMonitorSetupCancel={chatWorkflows.closeMonitorSetup}
        showCampaignCreation={chatWorkflows.showCampaignCreation}
        campaignContext={campaignContext}
        onCampaignCreated={handleCampaignCreated}
        onCampaignCancel={handleCampaignCancel}
        onCampaignEnrichData={handleCampaignEnrichData}
        onEnterRowSelectionMode={rowSelection.enterRowSelectionMode}
        onExitRowSelectionMode={rowSelection.exitRowSelectionMode}
        onSelectAllRows={rowSelection.selectAllRows}
        onSelectFirstNRows={rowSelection.selectFirstNRows}
        showEnrichmentFlow={chatWorkflows.showEnrichmentFlow}
        enrichmentContext={enrichmentContext}
        onEnrichmentComplete={chatWorkflows.closeEnrichmentFlow}
        onEnrichmentCancel={chatWorkflows.closeEnrichmentFlow}
        onEnrichmentStarted={handleEnrichmentStarted}
        showFindMoreFlow={chatWorkflows.showFindMoreFlow}
        findMoreContext={findMoreContext}
        onFindMoreComplete={chatWorkflows.closeFindMoreFlow}
        onFindMoreCancel={chatWorkflows.closeFindMoreFlow}
        onFindMoreStarted={handleFindMoreStarted}
      />
    </div>
  );
};
